import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicVisualizer, toAssetUrl, downloadProductsPdf } from "../services/api";
import ShowroomFilters from "../components/DigitalShowroom/ShowroomFilters";
import ProductCard from "../components/DigitalShowroom/ProductCard";

function adaptProduct(product) {
  const firstImage =
    Array.isArray(product.images) && product.images.length > 0
      ? product.images[0].url
      : null;

  const widthMm = product.size?.width ? Number(product.size.width) : null;
  const heightMm = product.size?.height ? Number(product.size.height) : null;

  return {
    id: product.id,
    name: product.sku || product.name || "Product",
    sku: product.sku,
    size:
      product.product_size ||
      (product.size
        ? `${product.size.width}x${product.size.height} ${product.size.unit}`
        : ""),
    image: toAssetUrl(firstImage),
    category: product.category,
    application_type: product.application_type,
    surface_type: product.surface_type,
    price: product.price,
    price_unit: product.price_unit,
    pieces_per_box: product.pieces_per_box,
    coverage_area_per_box: product.coverage_area_per_box,
    width_mm: widthMm,
    height_mm: heightMm,
  };
}

export default function DigitalShowroom() {
  const { visualizerToken } = useParams();
  const navigate = useNavigate();

  const [retailer, setRetailer] = useState(null);
  const [products, setProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  
  // PDF Selection States
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleToggleFilter = (filter) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
    setPage(1);
  };

  const handleSeeInRoom = () => {
    if (visualizerToken) {
      navigate(`/visualizer/${visualizerToken}`);
    }
  };

  // Toggle selection for a single product
  const handleToggleSelectProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  // Select/Deselect all visible products on current page
  const handleSelectAllCurrentPage = () => {
    const currentPageIds = products.map((p) => p.id);
    const areAllSelected = currentPageIds.every((id) =>
      selectedProductIds.includes(id)
    );

    if (areAllSelected) {
      setSelectedProductIds((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      setSelectedProductIds((prev) => [
        ...new Set([...prev, ...currentPageIds]),
      ]);
    }
  };

  // Download PDF logic
  const handleDownloadPdf = async () => {
    if (selectedProductIds.length === 0) return;
    setIsGeneratingPdf(true);

    try {
      // Call API helper to download PDF blob
      await downloadProductsPdf(visualizerToken, selectedProductIds);
    } catch (err) {
      console.error("Failed to download PDF", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Exit selection mode
  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedProductIds([]);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCatalog = async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const res = await getPublicVisualizer(visualizerToken, {
          page,
          limit,
          search: debouncedSearch,
          filters: activeFilters,
        });

        if (cancelled) return;
        const payload = res?.data || res;

        setRetailer(payload?.retailer || null);
        const rawProducts = payload?.products || [
          ...(payload?.tiles || []),
          ...(payload?.wallpapers || []),
        ];
        setProducts(rawProducts.map(adaptProduct));
        setTotalPages(payload?.pagination?.total_pages || 1);
        setTotalProducts(payload?.pagination?.total_items || 0);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCatalogError(
            "This visualizer link is invalid or no longer active."
          );
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    if (visualizerToken) fetchCatalog();
    return () => {
      cancelled = true;
    };
  }, [visualizerToken, page, limit, debouncedSearch, activeFilters]);

  if (catalogLoading && page === 1) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading products…</div>
      </div>
    );
  }

  if (catalogError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Showroom Unavailable
          </h1>
          <p className="text-gray-600 text-sm">{catalogError}</p>
        </div>
      </div>
    );
  }

  const allPageSelected =
    products.length > 0 &&
    products.every((p) => selectedProductIds.includes(p.id));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      {/* Top Navbar */}
      <header className="bg-white border-b border-gray-200 px-6 py-3.5 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          {retailer?.logo_url ? (
            <img
              src={toAssetUrl(retailer.logo_url)}
              alt={retailer?.company_name || "Company Logo"}
              className="h-9 max-w-[180px] object-contain"
            />
          ) : (
            <div>
              <h2 className="text-base font-extrabold tracking-tight text-gray-900 uppercase">
                {retailer?.company_name || "COMPANY NAME"}
              </h2>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => navigate(`/visualizer/${visualizerToken}`)}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 transition-colors inline-flex items-center gap-1.5 shadow-sm"
        >
          Go to Visualizer ↗
        </button>
      </header>

      {/* Main Container */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Filters */}
        <ShowroomFilters
          activeFilters={activeFilters}
          onToggleFilter={handleToggleFilter}
        />

        {/* Content Body */}
        <main className="flex-1 overflow-y-auto p-6 flex flex-col justify-between">
          <div>
            {/* Header & Controls */}
            <div className="mb-6 space-y-4">
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Explore our Products
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Visualize any product in your space with a single click and
                  share it instantly.
                </p>
              </div>

              {/* Search Bar & Action Buttons */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <svg
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-gray-200 rounded-lg outline-none focus:border-slate-800 transition-colors"
                  />
                </div>

                {!isSelectionMode ? (
                  <button
                    type="button"
                    onClick={() => setIsSelectionMode(true)}
                    className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors inline-flex items-center gap-1.5"
                  >
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      />
                    </svg>
                    Share product pdf
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    {/* Select All Checkbox Button */}
                    <button
                      type="button"
                      onClick={handleSelectAllCurrentPage}
                      className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={() => {}} // Handled by parent button click
                        className="w-3.5 h-3.5 rounded border-gray-300 text-slate-800 focus:ring-slate-800 cursor-pointer"
                      />
                      Select All ({products.length})
                    </button>

                    {/* Download Selected PDF */}
                    <button
                      type="button"
                      disabled={selectedProductIds.length === 0 || isGeneratingPdf}
                      onClick={handleDownloadPdf}
                      className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5 shadow-sm"
                    >
                      {isGeneratingPdf
                        ? "Generating PDF..."
                        : `Download PDF (${selectedProductIds.length})`}
                    </button>

                    {/* Cancel Selection Mode */}
                    <button
                      type="button"
                      onClick={handleCancelSelection}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-semibold transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Product Grid */}
            {catalogLoading ? (
              <div className="py-12 text-center text-xs text-gray-400">
                Updating products...
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product) => (
                  <div key={product.id} className="relative group">
                    {/* Checkbox Overlay when in Selection Mode */}
                    {isSelectionMode && (
                      <div
                        onClick={() => handleToggleSelectProduct(product.id)}
                        className={`absolute inset-0 z-10 rounded-xl cursor-pointer border-2 transition-all flex items-start justify-end p-3 ${
                          selectedProductIds.includes(product.id)
                            ? "border-slate-800 bg-slate-900/10"
                            : "border-transparent bg-black/5 hover:bg-black/10"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => {}} // Handled by overlay div
                          className="w-5 h-5 rounded border-gray-300 text-slate-800 focus:ring-slate-800 shadow cursor-pointer"
                        />
                      </div>
                    )}

                    <ProductCard
                      product={product}
                      visualizerToken={visualizerToken}
                      onSeeInRoom={handleSeeInRoom}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 text-sm">
                  {searchTerm
                    ? "No products match your search."
                    : "No products available."}
                </p>
              </div>
            )}
          </div>

          {/* Backend Pagination Bar */}
          {totalPages > 1 && (
            <div className="mt-8 pt-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing {products.length} of {totalProducts} products
              </span>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                <span className="text-xs font-semibold text-gray-700 px-2">
                  Page {page} of {totalPages}
                </span>

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}